import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Avatar,
  CardMedia,
  Button,
  Grid2,
  Skeleton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Header from "../../../components/Header/Header";
import Footer from "../../../components/Footer/Footer";
import { collection, getDocs } from "firebase/firestore"; // Import Firestore methods
import { db } from "../../../firestore-DB-config/firebase-config"; // Import your Firestore config
import BlogCard from "../../../components/blog-card/blogCard";
import { useLoader } from "../../../components/loader/loader-context";

interface Blog {
  id: string;
  date: string;
  title: string;
  cardDescription: string;
  description: string;
  author: string;
  datePublished: string;
  blogImageUrl: string;
  authorImageUrl: string;
  categories: string[];
  slug: string;
}

function BlogDetail() {
  const { slug } = useParams<{ slug: string }>(); // Get the blog slug from the URL
  const [blogPosts, setBlogPosts] = useState<Blog[]>([]); // State to store blog posts
  const [loading, setLoading] = useState(true); // Loading state
  const { showLoader, hideLoader } = useLoader();

  // Refactor fetchBlogs to use async/await
  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        showLoader(); // Start loader before fetching data
        const querySnapshot = await getDocs(collection(db, "blogs")); // Use await to fetch docs
        const blogsData = querySnapshot.docs.map((doc) => ({
          id: doc.id, // Get the document ID
          ...doc.data(), // Spread the document data
        })) as Blog[];

        setBlogPosts(blogsData);
      } catch (error) {
        console.error("Error fetching blogs:", error); // Log the error
      } finally {
        setLoading(false); // Set loading to false regardless of success or error
        hideLoader(); // Hide the loader when done
      }
    };

    fetchBlogs(); // Call the async function
  }, []);

  // Find the blog based on the id from the URL
  const blog = blogPosts.find((b) => b.slug === slug); // Match by slug

  if (loading) {
    return (
      <>
        <Header />
        {[1, 2, 3, 4].map((i) => (
          <Box key={i} sx={{ my: 6 }}>
            <Skeleton variant="text" width="60%" height={40} />
            <Skeleton variant="text" width="80%" height={30} />
            <Skeleton variant="text" width="90%" height={30} />
          </Box>
        ))}
        <Footer />
      </>
    );
  }

  if (!blog) {
    return <Typography variant="h4">Blog not found</Typography>;
  }

  // Filter random related blogs
  const randomRelatedBlogs = blogPosts
    .filter((b) => b.id !== blog.id) // Exclude the current blog
    .sort(() => 0.5 - Math.random()) // Randomize the order
    .slice(0, 3); // Pick the first 3 blogs

  return (
    <>
      <Header />

      <Box
        sx={{
          maxWidth: "85%",
          margin: "40px auto",
          padding: "20px",
          backgroundColor: "white",
        }}
      >
        <Button
          onClick={() => window.history.back()}
          style={{ textDecoration: "none" }}
          sx={{
            mb: 2,
            display: "inline-flex",
            alignItems: "center",
            color: "#3B3C4A",
            fontSize: "1rem",
            textDecoration: "none",
          }}
          startIcon={<ArrowBackIcon sx={{ fontSize: 20 }} />} // Back icon
        >
          Back {/* Button Text */}
        </Button>

        {/* Blog Title */}
        <Typography variant="h4" component="h1" fontWeight="600" gutterBottom>
          {blog.title}
        </Typography>

        {/* Author Info */}
        <Box display="flex" alignItems="center" mb={3}>
          <Avatar
            src={blog.authorImageUrl}
            sx={{ width: 40, height: 40, mr: 1 }}
          />
          <Box>
            <Typography variant="body1" color="text.secondary" fontWeight="500">
              {blog.author}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary" ml="35px">
              {blog.datePublished}
            </Typography>
          </Box>
        </Box>

        {/* Blog Image */}
        <CardMedia
          component="img"
          image={blog.blogImageUrl}
          alt={blog.title}
          sx={{
            height: 800,
            objectFit: "fill",
            borderRadius: "10px",
            mb: 4,
          }}
        />

        {/* Blog Content */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" lineHeight={1.7} paragraph>
            {/* Render the description using dangerouslySetInnerHTML to parse the HTML */}
            <span dangerouslySetInnerHTML={{ __html: blog.description }} />
          </Typography>
        </Box>

        {/* Related Blogs Section */}
        {randomRelatedBlogs.length > 0 && (
          <Box mt={6}>
            <Typography variant="h5" fontWeight="600" mb={3}>
              Related Blogs
            </Typography>

            <Grid2
              container
              spacing={{ xs: 3, sm: 6 }}
              justifyContent={{ xs: "center", md: "left" }}
            >
              {randomRelatedBlogs.map((relatedBlog) => (
                <Grid2 key={relatedBlog.id} size={{ xs: 10, md: 6, lg: 4 }}>
                  <BlogCard blog={relatedBlog} />
                </Grid2>
              ))}
            </Grid2>
          </Box>
        )}
      </Box>
      <Footer />
    </>
  );
}

export default BlogDetail;
